// @ts-nocheck
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import FlexWrapper, { FlexWrapperProps, FlexWrapperStyles } from "@store/Library/Components/Toolkit/FlexWrapper";
import { boxStickyStyles } from "@store/Themes/common/mixins/box";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint, { BreakpointArguments } from "@toolkit/Framework/Common/Utilities/breakpoint";
import isBreakpoint from "@toolkit/Framework/Common/Utilities/isBreakpoint";
import clearEmptyChild from "@toolkit/Framework/Common/Utilities/react/clearEmptyChild";
import { useSiteContext } from "@toolkit/Framework/Components/SiteContext";
import { useWindowContext } from "@toolkit/Framework/Components/WindowContext";
import React, { Children, FC, PropsWithChildren, useEffect, useRef } from "react";
import { css, useTheme } from "styled-components";

export const orderStickyActionBreakpoint: BreakpointKey = "sm";
export const orderStickyActionBreakpointArguments: BreakpointArguments = [orderStickyActionBreakpoint, "down"];

export interface OrderStickyActionProps extends ExpandStyles<OrderStickyActionStyles> {
    gap?: FlexWrapperProps["gap"];
    verticalGap?: FlexWrapperProps["verticalGap"];
}

export interface OrderStickyActionStyles {
    flexWrapper?: FlexWrapperStyles;
}

export const orderStickyActionStyles: OrderStickyActionStyles = {
    flexWrapper: {
        wrapper: {
            css: css`
                ${breakpoint(...orderStickyActionBreakpointArguments)} {
                    ${boxStickyStyles()}
                    position: fixed;
                    left: 0;
                    bottom: 0;
                    width: 100%;
                    z-index: ${x => x.theme.zIndex.stickyFooter};
                }
            `,
        },
        content: {
            css: css`
                ${breakpoint(...orderStickyActionBreakpointArguments)} {
                    flex-wrap: wrap;
                }
            `,
        },
        item: {
            css: css`
                width: 100%;

                ${breakpoint(...orderStickyActionBreakpointArguments)} {
                    width: auto;
                    flex-grow: 1;
                }
            `,
        },
    },
};

const OrderStickyAction: FC<PropsWithChildren<OrderStickyActionProps>> = ({
    extendedStyles,
    overridedStyles,
    ...props
}) => {
    const styles = useExpandProps(orderStickyActionStyles, extendedStyles, overridedStyles);
    const theme = useTheme();
    const { clientWidth, currentBreakpoint } = useWindowContext();
    const { setBottomSpacing } = useSiteContext();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const gap = props.gap || theme.design.form.actions.gap;
    const verticalGap = props.verticalGap || theme.design.form.actions.verticalGap;

    useEffect(() => {
        const height = wrapperRef.current?.offsetHeight;

        if (height) {
            setBottomSpacing(
                isBreakpoint(theme, currentBreakpoint, ...orderStickyActionBreakpointArguments) ? height : 0,
            );
        }
    }, [clientWidth, theme]);

    useEffect(() => {
        return () => setBottomSpacing(0);
    }, []);

    const children = clearEmptyChild(props.children);
    if (!Children.count(children)) {
        return null;
    }

    return (
        <FlexWrapper gap={gap} verticalGap={verticalGap} extendedStyles={styles.flexWrapper} ref={wrapperRef}>
            {children}
        </FlexWrapper>
    );
};

export default OrderStickyAction;
